/**
 * ResultsStep.jsx
 * ---------------
 * The final screen of the assessment. Displays results and handles
 * PDF generation and HubSpot submission.
 *
 * RESPONSIBILITIES:
 *   - Render the maturity wheel chart (custom SVG — see MaturityWheelChart below)
 *   - Display the combined organisational profile (name + narrative)
 *   - Display per-domain result cards (focus domain first)
 *   - Capture the chart as a PNG image for the PDF
 *   - Generate the PDF using pdfGenerator.js
 *   - Submit contact data, PDF and HubSpot note via hubspot.js
 *   - Handle loading, success and error states clearly
 *
 * PROPS:
 *   results {Object} - The full results object from calculateResults() in scoring.js
 *
 * CHART NOTES:
 *   MaturityWheelChart uses a 600×600 viewBox with OUTER_R at 38% of size (228px).
 *   Labels sit in the outer margin band (228–290px from centre) — well inside the
 *   viewBox — so they can never clip. The wrapper div enforces aspectRatio: "1/1"
 *   so the SVG is always square regardless of container width.
 *
 * Connected files:
 *   - scoring.js       provides the results object (calculateResults output)
 *   - pdfGenerator.js  generatePDF() called here
 *   - hubspot.js       submitToHubSpot() called here
 *   - cawood-assessment.css
 */

import { useRef, useState, useEffect } from "react";
import html2canvas from "html2canvas";
import { generatePDF } from "../utils/pdfGenerator.js";
import { submitToHubSpot } from "../utils/hubspot.js";
import { buildHubSpotNote } from "../utils/scoring.js";
import logoSrc from "../assets/cawood-logo.png";

// ─── MaturityWheelChart ───────────────────────────────────────────────────────

/**
 * Renders a circular maturity wheel with one coloured wedge per domain.
 * Each wedge is divided into five concentric rings (one per maturity level).
 * Rings are filled up to the domain score; rings beyond are ghost tracks.
 *
 * viewBox is 600×600. OUTER_R = 228px (38% of 600).
 * Labels sit at LABEL_R = 258px — inside the viewBox, never clipped.
 * The wrapper enforces aspectRatio 1:1 so the SVG is always circular.
 */
function MaturityWheelChart({ domains, size = 520 }) {
  // Fixed viewBox dimensions — do not change these without adjusting all radii
  const VB       = 600;
  const cx       = VB / 2;   // 300
  const cy       = VB / 2;   // 300
  const OUTER_R  = 228;      // outermost ring edge
  const INNER_R  = 68;       // hub radius
  const GAP_DEG  = 3;        // gap between wedges in degrees
  const LEVELS   = 5;
  const ringWidth = (OUTER_R - INNER_R) / LEVELS;

  const LABEL_R  = OUTER_R + 38;   // label midpoint — 266px from centre, inside 300px viewBox boundary
  const SCORE_R  = OUTER_R - ringWidth * 0.55;

  const count    = domains.length;
  const sliceDeg = 360 / count;

  function polar(r, angleDeg) {
    const rad = ((angleDeg - 90) * Math.PI) / 180;
    return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
  }

  function arcPath(innerR, outerR, startDeg, endDeg) {
    const p1 = polar(outerR, startDeg);
    const p2 = polar(outerR, endDeg);
    const p3 = polar(innerR, endDeg);
    const p4 = polar(innerR, startDeg);
    const large = endDeg - startDeg > 180 ? 1 : 0;
    return [
      `M ${p1.x} ${p1.y}`,
      `A ${outerR} ${outerR} 0 ${large} 1 ${p2.x} ${p2.y}`,
      `L ${p3.x} ${p3.y}`,
      `A ${innerR} ${innerR} 0 ${large} 0 ${p4.x} ${p4.y}`,
      "Z",
    ].join(" ");
  }

  function partialArcPath(innerR, outerR, startDeg, endDeg, fraction) {
    const partialInner = innerR + (outerR - innerR) * (1 - fraction);
    return arcPath(partialInner, outerR, startDeg, endDeg);
  }

  return (
    <svg
      viewBox={`0 0 ${VB} ${VB}`}
      width={size}
      height={size}
      style={{ display: "block", margin: "0 auto", maxWidth: "100%", height: "auto", overflow: "visible" }}
      aria-label="Digital maturity wheel chart"
      role="img"
    >
      {/* Subtle background fill */}
      <circle cx={cx} cy={cy} r={OUTER_R + 4} fill="#F7F7F7" />

      {domains.map((domain, i) => {
        const wedgeStart = i * sliceDeg + GAP_DEG / 2;
        const wedgeEnd   = (i + 1) * sliceDeg - GAP_DEG / 2;
        const score      = Math.min(Math.max(domain.displayScore, 0), 5);
        const fullRings  = Math.floor(score);
        const fraction   = score - fullRings;
        const midAngle   = (wedgeStart + wedgeEnd) / 2;

        const elements = [];

        // Ghost track rings
        for (let ring = 0; ring < LEVELS; ring++) {
          const rInner = INNER_R + ring * ringWidth;
          const rOuter = INNER_R + (ring + 1) * ringWidth - 1.5;
          elements.push(
            <path key={`ghost-${ring}`} d={arcPath(rInner, rOuter, wedgeStart, wedgeEnd)}
              fill={domain.color} opacity={0.12} />
          );
        }

        // Filled rings
        for (let ring = 0; ring < fullRings; ring++) {
          const rInner = INNER_R + ring * ringWidth;
          const rOuter = INNER_R + (ring + 1) * ringWidth - 1.5;
          elements.push(
            <path key={`filled-${ring}`} d={arcPath(rInner, rOuter, wedgeStart, wedgeEnd)}
              fill={domain.color} opacity={0.92} />
          );
        }

        // Partial ring at score tip
        if (fraction > 0.05 && fullRings < LEVELS) {
          const rInner = INNER_R + fullRings * ringWidth;
          const rOuter = INNER_R + (fullRings + 1) * ringWidth - 1.5;
          elements.push(
            <path key="partial" d={partialArcPath(rInner, rOuter, wedgeStart, wedgeEnd, fraction)}
              fill={domain.color} opacity={0.92} />
          );
        }

        // Domain label — split label words across two lines
        const labelPos   = polar(LABEL_R, midAngle);
        const words      = domain.label.split(" ");
        const mid        = Math.ceil(words.length / 2);
        const line1      = words.slice(0, mid).join(" ");
        const line2      = words.slice(mid).join(" ");
        const hasLine2   = words.length > 1;

        // Text anchor based on position around the wheel
        const isLeft  = midAngle > 180 && midAngle < 360;
        const isTop   = midAngle < 30 || midAngle > 330;
        const isBot   = midAngle > 150 && midAngle < 210;
        let anchor = "middle";
        if (!isTop && !isBot) anchor = isLeft ? "end" : "start";

        elements.push(
          <text key="label" x={labelPos.x} y={labelPos.y + (hasLine2 ? -7 : 0)}
            textAnchor={anchor} dominantBaseline="middle"
            style={{ fontFamily: "Exo 2, sans-serif", fontSize: "15px", fontWeight: 700,
              fill: domain.color === "#1C2541" ? "#1C2541" : domain.color }}>
            {line1}
            {hasLine2 && <tspan x={labelPos.x} dy="18">{line2}</tspan>}
          </text>
        );

        // Score label inside wedge tip — only if filled enough to be legible
        if (score >= 2.5) {
          const scorePos = polar(SCORE_R, midAngle);
          elements.push(
            <text key="score" x={scorePos.x} y={scorePos.y}
              textAnchor="middle" dominantBaseline="middle"
              style={{ fontFamily: "Exo 2, sans-serif", fontSize: "17px",
                fontWeight: 700, fill: "#FFFFFF" }}>
              {score.toFixed(1)}
            </text>
          );
        }

        return <g key={domain.id}>{elements}</g>;
      })}

      {/* Ring level separators */}
      {[1, 2, 3, 4, 5].map((level) => (
        <circle key={level} cx={cx} cy={cy} r={INNER_R + level * ringWidth}
          fill="none" stroke="#FFFFFF" strokeWidth={0.8} opacity={0.35} />
      ))}

      {/* Hub */}
      <circle cx={cx} cy={cy} r={INNER_R} fill="#1C2541" />
      <text x={cx} y={cy - 10} textAnchor="middle" dominantBaseline="middle"
        style={{ fontFamily: "Exo 2, sans-serif", fontSize: "13px", fontWeight: 400,
          fill: "#F7F7F7", opacity: 0.75 }}>
        Maturity
      </text>
      <text x={cx} y={cy + 12} textAnchor="middle" dominantBaseline="middle"
        style={{ fontFamily: "Exo 2, sans-serif", fontSize: "13px", fontWeight: 400,
          fill: "#F7F7F7", opacity: 0.75 }}>
        Profile
      </text>
    </svg>
  );
}

// ─── Sub-component: CombinedProfileCard ──────────────────────────────────────

/**
 * Renders the organisational profile card — profile name, weak/strong domain
 * pills, and the combined narrative. Shown on the results screen between the
 * chart and the per-domain cards.
 */
function CombinedProfileCard({ combinedProfile }) {
  if (!combinedProfile) return null;

  const { profileName, weakDomain1, weakDomain2, strongDomain, narrative } = combinedProfile;
  const paragraphs = narrative.split("\n\n");

  return (
    <div className="ca-card" style={{ marginBottom: "var(--ca-spacing-lg)" }}>

      {/* Header */}
      <div style={{
        background: "#1C2541",
        borderRadius: "var(--ca-border-radius-lg) var(--ca-border-radius-lg) 0 0",
        padding: "var(--ca-spacing-md) var(--ca-spacing-lg)",
        marginBottom: "var(--ca-spacing-lg)",
      }}>
        <div style={{ fontSize: "0.75rem", fontFamily: "var(--ca-font-heading)",
          fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase",
          color: "#FF6F61", marginBottom: "6px" }}>
          Your organisational profile
        </div>
        <div style={{ fontSize: "1.5rem", fontFamily: "var(--ca-font-heading)",
          fontWeight: 700, color: "#FFFFFF", lineHeight: 1.2 }}>
          {profileName}
        </div>
      </div>

      <div style={{ padding: "0 var(--ca-spacing-lg) var(--ca-spacing-lg)" }}>

        {/* Domain pills */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px",
          marginBottom: "var(--ca-spacing-lg)" }}>
          <span style={{ background: "#FF6F61", color: "#fff", borderRadius: "4px",
            padding: "3px 10px", fontSize: "0.8rem", fontWeight: 700,
            fontFamily: "var(--ca-font-heading)" }}>
            {weakDomain1.label} — {weakDomain1.score}/5
          </span>
          <span style={{ background: "#FF9E94", color: "#fff", borderRadius: "4px",
            padding: "3px 10px", fontSize: "0.8rem", fontWeight: 700,
            fontFamily: "var(--ca-font-heading)" }}>
            {weakDomain2.label} — {weakDomain2.score}/5
          </span>
          <span style={{ background: "#0CF57E", color: "#1C2541", borderRadius: "4px",
            padding: "3px 10px", fontSize: "0.8rem", fontWeight: 700,
            fontFamily: "var(--ca-font-heading)" }}>
            Strength: {strongDomain.label} — {strongDomain.score}/5
          </span>
        </div>

        {/* Narrative paragraphs */}
        {paragraphs.map((para, i) => (
          <p key={i} style={{ fontSize: "0.95rem", color: "#3A506B",
            fontFamily: "var(--ca-font-body)", lineHeight: 1.7,
            marginBottom: i < paragraphs.length - 1 ? "var(--ca-spacing-md)" : 0 }}>
            {para.trim()}
          </p>
        ))}

      </div>
    </div>
  );
}

// ─── Sub-component: DomainResultCard ─────────────────────────────────────────

function DomainResultCard({ domain }) {
  return (
    <div className={["ca-domain-result",
      domain.isFocusDomain ? "ca-domain-result--focus" : ""].join(" ")}>

      <div className="ca-domain-result__header">
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span style={{ display: "inline-block", width: "12px", height: "12px",
            borderRadius: "50%", background: domain.color, flexShrink: 0 }} />
          <h3 className="ca-domain-result__title">
            {domain.label}
            {domain.isFocusDomain && (
              <span className="ca-badge--focus">Your focus area</span>
            )}
          </h3>
        </div>
        <div className="ca-domain-result__score">{domain.displayScore}/5</div>
      </div>

      <div className="ca-domain-result__body">
        <div className="ca-domain-result__level">{domain.levelLabel}</div>
        <div className="ca-domain-result__narrative">{domain.narrative}</div>
        {domain.perceptionFlagged && (
          <div className="ca-domain-result__gap">
            <span className="ca-domain-result__gap-label">Perception gap</span>
            {domain.perceptionDelta > 0
              ? "You rated your team higher than you believe the rest of your organisation would. This is worth exploring in a wider conversation."
              : "You rated your team lower than you believe the rest of your organisation would. Your team may be applying a more critical — and more accurate — lens."}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main component: ResultsStep ─────────────────────────────────────────────

const CORE_API = "https://api.cawood.io";

export default function ResultsStep({ results }) {

  const radarRef = useRef(null);

  const [pdfUrl,      setPdfUrl]      = useState(null);
  const [pdfBase64,   setPdfBase64]   = useState(null);
  const [pdfFilename, setPdfFilename] = useState(null);
  const [submitting,  setSubmitting]  = useState(false);
  const [submitted,   setSubmitted]   = useState(false);
  const [downloadError, setDownloadError] = useState(null);

  // Auth gate state
  const [authState,   setAuthState]   = useState("checking"); // checking | unauthenticated | authenticated
  const [authMode,    setAuthMode]    = useState("login"); // register | login
  const [authLoading, setAuthLoading] = useState(false);
  const [authError,   setAuthError]   = useState(null);
  const [authForm,    setAuthForm]    = useState({ firstName: "", lastName: "", email: "", password: "" });

  const { domains, averageScore, respondent, combinedProfile } = results;

  const sortedDomains = [
    ...domains.filter((d) => d.isFocusDomain),
    ...domains.filter((d) => !d.isFocusDomain),
  ];

  // ── Submit to HubSpot and D1 on mount ──────────────────────────────────────
  // Fires as soon as the results screen is shown — does not wait for the
  // user to click download. PDF is generated here too so it's ready to go.
  useEffect(() => {
    async function submitOnMount() {
      // Step 1: Capture chart as PNG
      let radarChartImg = null;
      if (radarRef.current) {
        try {
          const canvas = await html2canvas(radarRef.current, {
            backgroundColor: "#FFFFFF",
            scale: 2,
          });
          radarChartImg = canvas.toDataURL("image/png");
        } catch (canvasErr) {
          console.warn("ResultsStep: chart capture failed", canvasErr);
        }
      }

      // Step 2: Load logo as base64
      let logoBase64 = null;
      try {
        const response = await fetch(logoSrc);
        const blob     = await response.blob();
        logoBase64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result.split(",")[1]);
          reader.onerror  = reject;
          reader.readAsDataURL(blob);
        });
      } catch (logoErr) {
        console.warn("ResultsStep: logo load failed", logoErr);
      }

      // Step 3: Generate PDF and cache it in state
      const { base64, filename } = generatePDF({ results, logoBase64, radarChartImg });
      setPdfBase64(base64);
      setPdfFilename(filename);

      // Step 4: Submit to HubSpot and D1
      const noteText      = buildHubSpotNote(results);
      const hubspotResult = await submitToHubSpot({ results, pdfBase64: base64, noteText });

      if (!hubspotResult.success) {
        console.warn("HubSpot submission failed:", hubspotResult.error);
        // Non-fatal — user can still download the PDF
      }

      // Store submission token and PDF for dashboard access
      if (hubspotResult.submissionToken) {
        try { sessionStorage.setItem("cawood_token", hubspotResult.submissionToken); }
        catch { /* sessionStorage unavailable — non-fatal */ }
      }
      try {
        sessionStorage.setItem("cawood_dma_pdf",      base64);
        sessionStorage.setItem("cawood_dma_filename", filename);
      } catch { /* sessionStorage unavailable or quota exceeded — non-fatal */ }

      // Check if user already has a valid session
      try {
        const sessionRes = await fetch(`${CORE_API}/auth/validate`, {
          method: "POST",
          credentials: "include",
        });
        const sessionData = await sessionRes.json();
        if (sessionData.valid && hubspotResult.submissionToken) {
          await fetch(`${CORE_API}/user/claim-submission`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ submission_token: hubspotResult.submissionToken }),
          }).catch(() => {}); // non-fatal
        }
        setAuthState(sessionData.valid ? "authenticated" : "unauthenticated");
      } catch {
        setAuthState("unauthenticated");
      }
    }

    submitOnMount().catch((err) => {
      console.error("ResultsStep: submitOnMount failed", err);
      setAuthState("unauthenticated");
    });
  }, []); // eslint-disable-line

  // ── Handle auth form submit ────────────────────────────────────────────────
  async function handleAuth(e) {
    e.preventDefault();
    setAuthLoading(true);
    setAuthError(null);

    const submissionToken = (() => {
      try { return sessionStorage.getItem("cawood_token"); } catch { return null; }
    })();

    try {
      if (authMode === "register") {
        const res = await fetch(`${CORE_API}/auth/register`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            email: authForm.email,
            password: authForm.password,
            first_name: authForm.firstName,
            last_name: authForm.lastName,
            submission_token: submissionToken,
          }),
        });
        const data = await res.json();
        if (!res.ok) {
          const msg = data.message || "Registration failed";
          const alreadyExists = res.status === 409 || msg.toLowerCase().includes("already") || msg.toLowerCase().includes("exist");
          throw new Error(alreadyExists
            ? "An account with that email already exists. Please log in instead."
            : msg);
        }
        window.location.href = "/account/dashboard/?from=assessment";

      } else {
        const res = await fetch(`${CORE_API}/auth/login`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ email: authForm.email, password: authForm.password }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.message || "Login failed");

        // Link submission to this user
        if (submissionToken) {
          await fetch(`${CORE_API}/user/claim-submission`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ submission_token: submissionToken }),
          }).catch(() => {}); // non-fatal
        }

        window.location.href = "/account/dashboard/?from=assessment";
      }
    } catch (err) {
      setAuthError(err.message);
    } finally {
      setAuthLoading(false);
    }
  }

  // ── Handle download button click ───────────────────────────────────────────
  async function handleDownload() {
    setSubmitting(true);
    setDownloadError(null);

    try {
      // If PDF isn't ready yet (very fast clicker), generate it now
      let base64   = pdfBase64;
      let filename = pdfFilename;

      if (!base64) {
        let radarChartImg = null;
        if (radarRef.current) {
          try {
            const canvas = await html2canvas(radarRef.current, {
              backgroundColor: "#FFFFFF",
              scale: 2,
            });
            radarChartImg = canvas.toDataURL("image/png");
          } catch (canvasErr) {
            console.warn("ResultsStep: chart capture failed", canvasErr);
          }
        }
        let logoBase64 = null;
        try {
          const response = await fetch(logoSrc);
          const blob     = await response.blob();
          logoBase64 = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result.split(",")[1]);
            reader.onerror  = reject;
            reader.readAsDataURL(blob);
          });
        } catch (logoErr) {
          console.warn("ResultsStep: logo load failed", logoErr);
        }
        const generated = generatePDF({ results, logoBase64, radarChartImg });
        base64   = generated.base64;
        filename = generated.filename;
      }

      // Trigger browser download
      const byteArray = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
      const blob      = new Blob([byteArray], { type: "application/pdf" });
      const url       = URL.createObjectURL(blob);
      setPdfUrl(url);
      const link      = document.createElement("a");
      link.href       = url;
      link.download   = filename;
      link.click();

      setSubmitted(true);

    } catch (err) {
      console.error("ResultsStep: download error", err);
      setDownloadError("Something went wrong generating your report. Please refresh the page and try again.");
    } finally {
      setSubmitting(false);
    }
  }

  // TODO: move inputStyle and all auth-form inline styles below to cawood-assessment.css
  // using ca-auth-form, ca-auth-form__toggle, ca-btn--toggle, ca-btn--toggle-active,
  // ca-results__status classes — follow the pattern established in cawood-personal.css.
  const inputStyle = {
    border: "1px solid #CBD5E0", borderRadius: "4px",
    padding: "10px 12px", fontSize: "0.95rem",
    fontFamily: "var(--ca-font-body)", width: "100%",
    boxSizing: "border-box", color: "#1C2541",
  };

  return (
    <div className="ca-assessment__step">

      {/* Results header */}
      <div className="ca-results__header">
        <h2>Your digital maturity profile</h2>
        <div className="ca-results__score">{averageScore}/5</div>
        <div className="ca-results__score-label">
          Overall score across {domains.length} domains
          &nbsp;·&nbsp;
          {respondent.organisation}
        </div>
      </div>

      {/* Maturity wheel chart
          The wrapper enforces a square aspect ratio so the SVG can never
          be stretched into an oval by a flex or block container. */}
      <div className="ca-radar">
        <h3>Maturity across six domains</h3>
        <div
          ref={radarRef}
          style={{
            background: "#fff",
            padding: "8px 64px",
            width: "100%",
            maxWidth: "520px",
            margin: "0 auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "visible",
          }}
        >
          <MaturityWheelChart domains={domains} size={520} />
        </div>

        {/* Legend */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px 24px",
          padding: "16px 8px 4px", justifyContent: "center" }}>
          {domains.map((d) => (
            <div key={d.id} style={{ display: "flex", alignItems: "center", gap: "6px",
              fontSize: "0.82rem", color: "#3A506B", fontFamily: "Open Sans, sans-serif" }}>
              <span style={{ width: "10px", height: "10px", borderRadius: "2px",
                background: d.color, flexShrink: 0 }} />
              {d.label}
              <span style={{ color: "#FF6F61", fontWeight: 700 }}>{d.displayScore}/5</span>
            </div>
          ))}
        </div>
      </div>

      {/* Organisational profile — shown between chart and domain cards */}
      <CombinedProfileCard combinedProfile={combinedProfile} />

      {/* Per-domain result cards */}
      <div>
        <h3 style={{ marginBottom: "var(--ca-spacing-md)" }}>Domain by domain</h3>
        {sortedDomains.map((domain) => (
          <DomainResultCard key={domain.id} domain={domain} />
        ))}
      </div>

      {/* Auth gate and download panel */}
      <div className="ca-results__actions">
        <h3>Get your full report</h3>
        <p>
          Save your results and download your personalised PDF report.
          Create a free account to access your results at any time.
        </p>

        {/* Checking session */}
        {authState === "checking" && (
          <div style={{ color: "#3A506B", fontSize: "0.9rem" }}>Checking your account…</div>
        )}

        {/* Already authenticated — show download */}
        {authState === "authenticated" && (
          <>
            {!submitted ? (
              <button type="button" className="ca-btn ca-btn--cta"
                onClick={handleDownload} disabled={submitting}>
                {submitting ? (
                  <><span className="ca-spinner" />Generating your report…</>
                ) : (
                  "Download my report →"
                )}
              </button>
            ) : (
              <>
                <div className="ca-alert ca-alert--success"
                  style={{ marginBottom: "var(--ca-spacing-md)" }}>
                  Your report has been downloaded. Log in at any time to access it again.
                </div>
                {pdfUrl && (
                  <a href={pdfUrl} download className="ca-btn ca-btn--ghost">Download again</a>
                )}
              </>
            )}
            {downloadError && <div className="ca-alert ca-alert--error">{downloadError}</div>}
          </>
        )}

        {/* Not authenticated — show register/login form */}
        {authState === "unauthenticated" && (
          <form onSubmit={handleAuth} style={{ marginTop: "var(--ca-spacing-md)" }}>

            {/* Mode toggle */}
            <div style={{ display: "flex", gap: "12px", marginBottom: "var(--ca-spacing-md)" }}>
              <button type="button"
                onClick={() => { setAuthMode("register"); setAuthError(null); }}
                style={{
                  background: authMode === "register" ? "#0CF57E" : "transparent",
                  color: authMode === "register" ? "#1C2541" : "#fff",
                  border: "2px solid " + (authMode === "register" ? "#0CF57E" : "rgba(255,255,255,0.4)"),
                  borderRadius: "4px", padding: "8px 20px",
                  fontFamily: "var(--ca-font-heading)",
                  fontWeight: 700, fontSize: "0.85rem", cursor: "pointer",
                  transition: "all 0.15s ease",
                }}>
                Create account
              </button>
              <button type="button"
                onClick={() => { setAuthMode("login"); setAuthError(null); }}
                style={{
                  background: authMode === "login" ? "#0CF57E" : "transparent",
                  color: authMode === "login" ? "#1C2541" : "#fff",
                  border: "2px solid " + (authMode === "login" ? "#0CF57E" : "rgba(255,255,255,0.4)"),
                  borderRadius: "4px", padding: "8px 20px",
                  fontFamily: "var(--ca-font-heading)",
                  fontWeight: 700, fontSize: "0.85rem", cursor: "pointer",
                  transition: "all 0.15s ease",
                }}>
                Log in
              </button>
            </div>

            {/* Name fields — register only */}
            {authMode === "register" && (
              <div style={{ display: "flex", gap: "12px", marginBottom: "12px" }}>
                <input type="text" placeholder="First name" required
                  value={authForm.firstName}
                  onChange={e => setAuthForm(f => ({ ...f, firstName: e.target.value }))}
                  style={inputStyle} />
                <input type="text" placeholder="Last name" required
                  value={authForm.lastName}
                  onChange={e => setAuthForm(f => ({ ...f, lastName: e.target.value }))}
                  style={inputStyle} />
              </div>
            )}

            <input type="email" placeholder="Email address" required
              value={authForm.email}
              onChange={e => setAuthForm(f => ({ ...f, email: e.target.value }))}
              style={{ ...inputStyle, marginBottom: "12px", display: "block", width: "100%" }} />

            <input type="password" placeholder="Password" required
              value={authForm.password}
              onChange={e => setAuthForm(f => ({ ...f, password: e.target.value }))}
              style={{ ...inputStyle, marginBottom: "16px", display: "block", width: "100%" }} />

            {authError && (
              <div className="ca-alert ca-alert--error" style={{ marginBottom: "12px" }}>
                {authError}
              </div>
            )}

            <button type="submit" className="ca-btn ca-btn--cta" disabled={authLoading}>
              {authLoading ? (
                <><span className="ca-spinner" />{authMode === "register" ? "Creating account…" : "Logging in…"}</>
              ) : (
                authMode === "register" ? "Create account & download →" : "Log in & download →"
              )}
            </button>
          </form>
        )}
      </div>

    </div>
  );
}
